import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import SEOMeta from '@/components/feature/SEOMeta';
import StructuredData from '@/components/feature/StructuredData';
import { getMovieById, getMoviesByCategory, categories, type CategoryType } from '@/mocks/movies';
import NotFound from '@/pages/NotFound';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id || '');

  const categoryInfo = categories.find(c => c.key === movie?.category);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return getMoviesByCategory(movie.category)
      .filter(m => m.id !== movie.id)
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return <NotFound />;
  }

  const siteUrl = 'https://777cinema.com';
  const pageUrl = `${siteUrl}/detail/${movie.id}`;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: categoryInfo?.label || '影视', item: `${siteUrl}/category/${movie.category}` },
        { '@type': 'ListItem', position: 3, name: movie.title, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Movie',
      name: movie.title,
      description: movie.description,
      image: movie.posterUrl,
      director: { '@type': 'Person', name: movie.director },
      actor: movie.actors.map(a => ({ '@type': 'Person', name: a })),
      dateCreated: `${movie.year}`,
      url: pageUrl,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${movie.title} - 777影院`,
      url: pageUrl,
      description: movie.description,
      isPartOf: { '@type': 'WebSite', name: '777影院', url: siteUrl },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `${movie.title}在哪里可以在线观看？`,
          acceptedAnswer: { '@type': 'Answer', text: `《${movie.title}》正在777影院免费在线播放，支持超清画质，无需付费和会员。` },
        },
        {
          '@type': 'Question',
          name: `${movie.title}的主演是谁？`,
          acceptedAnswer: { '@type': 'Answer', text: `《${movie.title}》由${movie.actors.slice(0, 3).join('、')}等主演，${movie.director}执导。` },
        },
        {
          '@type': 'Question',
          name: `${movie.title}的评分是多少？`,
          acceptedAnswer: { '@type': 'Answer', text: `《${movie.title}》在777影院的评分为${movie.rating}分（满分10分），广受观众好评。` },
        },
      ],
    },
  ];

  return (
    <>
      <SEOMeta
        title={`${movie.title} 在线观看 - 777影院高清免费`}
        description={`《${movie.title}》${movie.description} 导演：${movie.director}，主演：${movie.actors.slice(0, 4).join('、')}。777影院提供${movie.title}超清免费在线观看，流畅不卡顿。`}
        keywords={`${movie.title},${movie.title}在线观看,${movie.title}免费,777影院,${movie.category === 'movie' ? '电影' : movie.category === 'tv' ? '电视剧' : movie.category === 'variety' ? '综艺' : '动漫'}在线观看`}
        canonicalUrl={pageUrl}
        ogTitle={`《${movie.title}》 - 777影院高清免费在线观看`}
        ogDescription={movie.description}
        ogImage={movie.posterUrl}
        ogType="video.movie"
      />
      <StructuredData data={structuredData} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: categoryInfo?.label || '影视', path: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />
          </div>

          <section className="py-6 md:py-10">
            <div className="w-full px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Poster */}
                <div className="shrink-0 mx-auto lg:mx-0">
                  <div className="w-52 md:w-64 rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={movie.posterUrl}
                      alt={`${movie.title} 在线观看`}
                      title={`${movie.title} - 777影院高清在线观看`}
                      className="w-full aspect-[2/3] object-cover object-top"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1">
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                    {movie.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-4">{movie.englishTitle}</p>

                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    {movie.tags.map((tag) => (
                      <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6 mb-6 text-sm">
                    <div>
                      <span className="text-foreground-500">导演：</span>
                      <span className="text-foreground-900 font-medium">{movie.director}</span>
                    </div>
                    <div>
                      <span className="text-foreground-500">主演：</span>
                      <span className="text-foreground-900 font-medium">{movie.actors.slice(0, 3).join('、')}</span>
                    </div>
                    <div>
                      <span className="text-foreground-500">年份：</span>
                      <span className="text-foreground-900 font-medium">{movie.year}</span>
                    </div>
                    <div>
                      <span className="text-foreground-500">地区：</span>
                      <span className="text-foreground-900 font-medium">{movie.region}</span>
                    </div>
                    <div>
                      <span className="text-foreground-500">时长/集数：</span>
                      <span className="text-foreground-900 font-medium">{movie.duration}</span>
                    </div>
                    <div>
                      <span className="text-foreground-500">更新：</span>
                      <span className="text-foreground-900 font-medium">{movie.updateDate}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg">
                        <i className="ri-star-fill text-accent-500 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-accent-600">{movie.rating}</div>
                        <div className="text-[10px] text-foreground-500">评分</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="bg-primary-500 hover:bg-primary-600 text-white font-medium px-6 py-2.5 rounded-full text-sm transition-colors duration-200 whitespace-nowrap flex items-center gap-2">
                        <i className="ri-play-fill"></i>
                        立即播放
                      </button>
                      <button className="bg-background-100 hover:bg-background-200 text-foreground-700 font-medium px-6 py-2.5 rounded-full text-sm transition-colors duration-200 whitespace-nowrap flex items-center gap-2">
                        <i className="ri-heart-line"></i>
                        收藏
                      </button>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-lg p-5">
                    <h3 className="font-heading font-semibold text-foreground-950 mb-3 text-sm">
                      <i className="ri-file-text-line mr-1.5 text-primary-500"></i>
                      剧情简介
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {movie.longDescription}
                    </p>
                  </div>

                  {movie.actors.length > 0 && (
                    <div className="mt-5">
                      <h3 className="font-heading font-semibold text-foreground-950 mb-3 text-sm">
                        <i className="ri-user-star-line mr-1.5 text-primary-500"></i>
                        演员阵容
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {movie.actors.map((actor) => (
                          <span key={actor} className="text-xs bg-background-100 text-foreground-700 px-3 py-1.5 rounded-lg whitespace-nowrap hover:bg-primary-50 hover:text-primary-600 cursor-default transition-colors">
                            {actor}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Related Movies */}
          {relatedMovies.length > 0 && (
            <section className="py-8 md:py-12 bg-background-100">
              <div className="w-full px-4 md:px-6">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-6">
                  <i className="ri-stack-line mr-2 text-primary-500"></i>
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
}